/* eslint-disable @typescript-eslint/no-shadow */
import {
  Editable,
  EditableInput,
  EditablePreview,
  Flex,
  Select,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { css } from '@emotion/react';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { DimItem } from 'models/dimensionItem.interface';
import { getItemId } from 'redux/ItemPageSlice';
import { usePlanPageRevampVariant } from 'pages/PlanPageDetailsOPZ/PlanPageUIRevamp/usePlanPageUiRevamp';
import { getPlanPageThemeColors } from 'redux/ThemeSlice';

interface DesktopValues {
  keys: string;
  value: string | any;
  onSubmitValue: (value: string, id: number, propertyValue: string) => void;
  dataElementsId: any;
  planPermission?: boolean;
  connectedDimensionItemsMap?: Record<number, any[]>;
  variant?: 'legacy' | 'revamp';
}

const styles = {
  border: css`
    &:last-child {
      border: none;
    }
  `,
};

const DesktopProperties = React.memo(
  ({
    keys,
    value,
    onSubmitValue,
    dataElementsId,
    planPermission,
    connectedDimensionItemsMap = {},
    variant = 'legacy',
  }: DesktopValues) => {
    const [inputValue, setInputValue] = useState(value);
    const [isEditMode, setIsEditMode] = useState(false);
    const isRevamp = usePlanPageRevampVariant(variant);
    const themeColors = useSelector(getPlanPageThemeColors);

    const itemId = useSelector(getItemId);

    const findConnectedDimId = () => {
      const connectedProperty = itemId?.properties?.find((property: any) => {
        return property.name === keys && property.data_format && property.type === 'dimension';
      });

      if (connectedProperty) {
        const dataFormat = JSON.parse(connectedProperty?.data_format || '{}');
        const dimensionId = dataFormat?.dimension;
        // Convert to number if it's a string (API returns dimension IDs as strings in JSON)
        return dimensionId ? Number(dimensionId) : null;
      }
      return null;
    };

    const connectedDimId = findConnectedDimId();
    // Get items from the map, ensuring we handle both number and string keys
    const getConnectedDimensionItems = (): DimItem[] | null => {
      if (!connectedDimId) return null;

      // Try number key first
      const items = connectedDimensionItemsMap[connectedDimId];
      if (items && Array.isArray(items) && items.length > 0) {
        return items as DimItem[];
      }

      // Fallback: try string key (in case of type mismatch)
      const stringKey = String(connectedDimId);
      const itemsByString = connectedDimensionItemsMap[stringKey as any];
      if (itemsByString && Array.isArray(itemsByString) && itemsByString.length > 0) {
        return itemsByString as DimItem[];
      }

      return null;
    };

    const connectedDimensionItems = getConnectedDimensionItems();
    // Sync inputValue with value prop when it changes
    useEffect(() => {
      setInputValue(value);
    }, [value]);

    if (keys === 'Department') {
      return null;
    }

    const submitDropdownData = (event: any, id: number, propertyValue: string) => {
      const newValue = event.target.value;
      setInputValue(newValue);
      onSubmitValue(newValue, id, propertyValue);
    };

    // Show dropdown if we have connected dimension items
    if (connectedDimensionItems && connectedDimensionItems.length > 0 && connectedDimId) {
      return (
        <Flex
          w='100%'
          justifyContent='space-between'
          p={isRevamp ? '12px 0' : '15px 0px'}
          alignItems='center'
          borderBottom={isRevamp ? '1px solid' : '1px solid rgba(255, 255, 255, 0.3)'}
          borderColor={isRevamp ? themeColors.border : undefined}
          css={styles.border}
        >
          <>
            <Tooltip label={keys}>
              <Text
                noOfLines={1}
                maxWidth='150px'
                fontSize={isRevamp ? '14px' : undefined}
                fontWeight={isRevamp ? '700' : undefined}
                fontFamily={isRevamp ? 'bold' : undefined}
                color={isRevamp ? themeColors.textDark : undefined}
              >
                {keys}
              </Text>
            </Tooltip>
            <Tooltip label={inputValue}>
              <Select
                maxWidth={isRevamp ? '112px' : '100px'}
                iconColor={isRevamp ? themeColors.textDark : 'white'}
                iconSize='small'
                borderRadius={isRevamp ? '8px' : '10px'}
                placeholder='Select option'
                value={inputValue || ''}
                color={isRevamp ? themeColors.textDark : 'white'}
                bg={isRevamp ? themeColors.surface : '#796cd6'}
                border={isRevamp ? '1px solid' : undefined}
                borderColor={isRevamp ? themeColors.border : undefined}
                fontSize={isRevamp ? '14px' : '2xs'}
                fontFamily={isRevamp ? 'bold' : undefined}
                p={isRevamp ? '8px 13px' : '2px'}
                h={isRevamp ? '36px' : '7.5'}
                onChange={(event: any) => submitDropdownData(event, dataElementsId, keys)}
                isDisabled={!!planPermission}
                cursor={planPermission ? 'not-allowed' : 'pointer'}
              >
                {connectedDimensionItems.map((item: DimItem) => (
                  <option
                    key={item.id}
                    value={item.name}
                    style={{ margin: '4px', borderRadius: '25px' }}
                  >
                    {item.name}
                  </option>
                ))}
              </Select>
            </Tooltip>
          </>
        </Flex>
      );
    }

    return (
      <Flex
        w='100%'
        justifyContent='space-between'
        p={isRevamp ? '12px 0' : '15px 0px'}
        alignItems='center'
        borderBottom={isRevamp ? '1px solid' : '1px solid rgba(255, 255, 255, 0.3)'}
        borderColor={isRevamp ? themeColors.border : undefined}
        css={styles.border}
      >
        <>
          <Tooltip label={keys}>
            <Text
              noOfLines={1}
              maxWidth='150px'
              fontSize={isRevamp ? '14px' : undefined}
              fontWeight={isRevamp ? '700' : undefined}
              fontFamily={isRevamp ? 'bold' : undefined}
              color={isRevamp ? themeColors.textDark : undefined}
            >
              {keys}
            </Text>
          </Tooltip>

          <Tooltip label={inputValue}>
            <Editable
              key={`${dataElementsId}-${keys}-${value}`}
              defaultValue={inputValue}
              h={isRevamp ? '36px' : '7.5'}
              flexGrow='1'
              borderWidth='1'
              borderRadius={isRevamp ? '8px' : 'xs'}
              borderColor={isRevamp ? themeColors.border : undefined}
              bg={isRevamp ? themeColors.surface : undefined}
              fontSize={isRevamp ? '14px' : 'xs'}
              fontFamily={isRevamp ? 'bold' : undefined}
              color={isRevamp ? themeColors.textDark : undefined}
              display='flex'
              alignItems='center'
              justifyContent='center'
              textAlign='center'
              onChange={(v) => setInputValue(v)}
              onSubmit={async () => {
                setIsEditMode(false);
                onSubmitValue(inputValue, dataElementsId, keys);
              }}
              maxWidth={isRevamp ? '112px' : '80px'}
              cursor={planPermission ? 'not-allowed' : 'pointer'}
              isDisabled={!!planPermission}
              onClick={() => setIsEditMode(true)}
            >
              <EditablePreview
                minW='50px'
                pl='0.25rem'
                pr='0.25rem'
                h='100%'
                fontFamily={isRevamp ? 'bold' : undefined}
                fontWeight={isRevamp ? 700 : undefined}
                display={isEditMode ? 'none' : 'inline'}
                overflow='hidden'
                whiteSpace='nowrap'
                textOverflow='ellipsis'
                cursor={planPermission ? 'not-allowed' : 'pointer'}
                onClick={() => setIsEditMode(true)}
              />
              <EditableInput
                pl='0.25rem'
                pr='0.25rem'
                h='100%'
                fontFamily={isRevamp ? 'bold' : undefined}
                fontWeight={isRevamp ? 700 : undefined}
                display={!isEditMode ? 'none' : 'inline !important'}
                cursor={planPermission ? 'not-allowed' : 'pointer'}
                _focusVisible={{
                  borderColor: 'none',
                }}
                autoFocus
              />
            </Editable>
          </Tooltip>
        </>
      </Flex>
    );
  },
);

DesktopProperties.displayName = 'DesktopProperties';

export default DesktopProperties;
